import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { 
  PlusIcon, 
  MagnifyingGlassIcon, 
  EyeIcon, 
  PencilIcon,
  PrinterIcon, // Import PrinterIcon
  ArrowPathIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import axios from '../../api/axiosConfig';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Pagination from '../../components/common/Pagination';

interface Client {
  id: number;
  name: string;
  director: string;
}

interface Contract {
  id: number;
  clientId: number;
  contractNumber: string;
  contractDate: string;
  startDate: string;
  endDate: string;
  status: 'draft' | 'sent' | 'signed' | 'active' | 'completed' | 'terminated';
  notes?: string;
  Client?: Client;
  createdAt: string;
  issueDate: string;
}

interface PaginatedContractsResponse {
  contracts: Contract[];
  totalPages: number;
  currentPage: number;
  totalCount: number;
}

const CONTRACT_STATUSES = {
  all: 'Все статусы',
  draft: 'Черновик',
  sent: 'Отправлен',
  signed: 'Подписан',
  active: 'Активный',
  completed: 'Завершен',
  terminated: 'Расторгнут'
};

const ContractList: React.FC = () => {
  const { can } = useAuth(); // Changed from isAllowed
  const [searchParams, setSearchParams] = useSearchParams();
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get('page')) || 1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [limit] = useState(15);

  // const canCreateContracts = can('contracts.create'); // Will use can('contracts.create') directly

  const fetchContracts = useCallback(async (page = currentPage, search = searchTerm, status = statusFilter) => {
    setLoading(true);
    setError(null);

    const params = new URLSearchParams();
    params.append('page', String(page));
    params.append('limit', String(limit));
    if (search) params.append('search', search);
    if (status && status !== 'all') params.append('status', status);

    setSearchParams(params, { replace: true });

    try {
      const response = await axios.get<PaginatedContractsResponse>('/contracts', { params });
      setContracts(response.data.contracts);
      setTotalPages(response.data.totalPages);
      setCurrentPage(response.data.currentPage);
      setTotalCount(response.data.totalCount);
    } catch (err) {
      console.error('Error fetching contracts:', err);
      setError('Ошибка при загрузке договоров');
      setContracts([]);
      setTotalPages(1);
      setCurrentPage(1);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [limit, setSearchParams, currentPage, searchTerm, statusFilter]);

  useEffect(() => {
    fetchContracts(currentPage, searchTerm, statusFilter);
  }, [fetchContracts, currentPage, searchTerm, statusFilter]);

  // Rename and modify the handler to open the print page
  const handleOpenPrintView = (id: number) => {
    const printUrl = `/contracts/${id}/print`;
    const printWindow = window.open(printUrl, '_blank');
    if (!printWindow) {
      alert('Не удалось открыть окно для печати. Пожалуйста, проверьте настройки блокировки всплывающих окон.');
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(event.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const resetFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setCurrentPage(1);
  };
  
  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    }).format(date);
  };
  
  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'sent':
        return 'bg-blue-100 text-blue-800';
      case 'signed':
        return 'bg-green-100 text-green-800';
      case 'active':
        return 'bg-purple-100 text-purple-800';
      case 'completed':
        return 'bg-gray-100 text-gray-800';
      case 'terminated':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  const getStatusText = (status: keyof typeof CONTRACT_STATUSES | string): string => {
    return CONTRACT_STATUSES[status as keyof typeof CONTRACT_STATUSES] || status;
  };
  
  if (loading && (!contracts || contracts.length === 0)) {
    return <LoadingSpinner />;
  }
  
  return (
    <div>
      <PageHeader
        title="Договоры"
        subtitle={`Всего найдено: ${totalCount}`}
        actions={
          can('contracts.create') ? ( // Changed from canManageContracts
            <Link
              to="/contracts/new"
              className="btn-primary flex items-center"
            >
              <PlusIcon className="h-5 w-5 mr-1" />
              Создать договор
            </Link>
          ) : null
        }
      />
      
      {error && (
        <div className="bg-red-100 text-red-700 p-4 rounded-md mb-4">
          {error}
        </div>
      )}
      
      <div className="mb-6 p-4 bg-gray-50 rounded-lg shadow-sm">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 items-end">
          <div className="sm:col-span-1 md:col-span-1">
            <label htmlFor="search" className="form-label">Поиск</label>
            <div className="relative">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none" />
              <input
                id="search"
                type="text"
                placeholder="Номер договора, клиент..."
                className="form-input pl-10 w-full"
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </div>
          </div>
          
          <div className="sm:col-span-1 md:col-span-1">
            <label htmlFor="status-filter" className="form-label">Статус</label>
            <select
              id="status-filter"
              className="form-input w-full"
              value={statusFilter}
              onChange={handleStatusFilterChange}
            >
              {Object.entries(CONTRACT_STATUSES).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>

          <div className="sm:col-span-2 md:col-span-1 flex justify-end">
            <button
              type="button"
              onClick={resetFilters}
              className="btn-secondary-outline inline-flex items-center text-sm"
              disabled={loading}
            >
              <XMarkIcon className="h-4 w-4 mr-1"/>
              Сбросить
            </button>
          </div>
        </div>
      </div>
      
      {loading && <LoadingSpinner />} 

      {!loading && !error && (!contracts || contracts.length === 0) && (
        <div className="bg-white rounded-lg shadow p-6 text-center">
          <p className="text-gray-500">
            {(searchTerm || statusFilter !== 'all') 
              ? 'Договоры, соответствующие критериям, не найдены.' 
              : 'Договоры еще не добавлены.'}
          </p>
          {can('contracts.create') && ( // Changed from canManageContracts
            <Link to="/contracts/new" className="mt-4 btn-primary inline-flex items-center">
              <PlusIcon className="h-5 w-5 mr-1" />
              Создать договор
            </Link>
          )}
        </div>
      )}

      {!loading && contracts && contracts.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  № договора
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Клиент
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Дата договора
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Период действия
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Статус
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Действия
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {contracts.map((contract) => (
                <tr key={contract.id} className="hover:bg-gray-50">
                  {/* Removed whitespace-nowrap, added break-words */}
                  <td className="px-6 py-4 break-words">
                    <div className="text-sm text-gray-900">
                      {contract.contractNumber || `Договор №${contract.id}`}
                    </div>
                  </td>
                  {/* Removed whitespace-nowrap, added break-words */}
                  <td className="px-6 py-4 break-words">
                    {contract.Client ? (
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          <Link to={`/clients/${contract.clientId}`} className="text-primary hover:text-primary-dark">
                            {contract.Client.name}
                          </Link>
                        </div>
                        <div className="text-sm text-gray-500">
                          {contract.Client.director}
                        </div>
                      </div>
                    ) : (
                      <span className="text-gray-500">-</span>
                    )}
                  </td>
                  {/* Removed whitespace-nowrap */}
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">
                      {formatDate(contract.issueDate)}
                    </div>
                  </td>
                  {/* Removed whitespace-nowrap */}
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">
                      {formatDate(contract.startDate)} — {formatDate(contract.endDate)}
                    </div>
                  </td>
                  {/* Removed whitespace-nowrap */}
                  <td className="px-6 py-4">
                    <span className={`px-2 inline-flex text-xs leading-5 font-medium rounded-full ${getStatusBadgeClass(contract.status)}`}>
                      {getStatusText(contract.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end space-x-2">
                      <Link
                        to={`/contracts/${contract.id}`}
                        className="text-gray-600 hover:text-gray-900"
                        title="Просмотр"
                      >
                        <EyeIcon className="h-5 w-5" />
                      </Link>

                      {/* Update button to use new handler and icon */}
                      <button
                        onClick={() => handleOpenPrintView(contract.id)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Печать" // Update title
                      >
                        <PrinterIcon className="h-5 w-5" /> {/* Use PrinterIcon */}
                      </button>

                      {can('contracts.edit') && // Changed from canManageContracts
                       (contract.status === 'draft' || contract.status === 'sent') && (
                        <Link
                          to={`/contracts/edit/${contract.id}`}
                          className="text-gray-600 hover:text-gray-900"
                          title="Редактировать"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </Link>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && totalPages > 1 && (
        <div className="mt-6">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </div>
  );
};

export default ContractList;
